import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { challengeApi, type TrackInfo, type ChallengePublicInfo } from '../utils/api'
import AuthHeader from '../components/AuthHeader'
import Logo from '../components/Logo'

const TRACK_COLORS: Record<string, { badge: string; accent: string }> = {
  beginner: { badge: 'bg-green-100 text-green-700', accent: 'text-green-600' },
  intermediate: { badge: 'bg-blue-100 text-blue-700', accent: 'text-blue-600' },
  advanced: { badge: 'bg-purple-100 text-purple-700', accent: 'text-purple-600' },
}

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  hard: 'bg-red-100 text-red-700',
}

function TrackPage() {
  const { trackId } = useParams<{ trackId: string }>()
  const [track, setTrack] = useState<TrackInfo | null>(null)
  const [challenges, setChallenges] = useState<ChallengePublicInfo[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!trackId) return
    setIsLoading(true)
    challengeApi.getTrackDetail(trackId)
      .then(r => {
        setTrack(r.track)
        setChallenges(r.challenges)
      })
      .catch(() => {})
      .finally(() => setIsLoading(false))
  }, [trackId])

  const colors = TRACK_COLORS[trackId || ''] || TRACK_COLORS.beginner

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-qcloud-border sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="small" />
              <span className="text-xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">
                QuantumArena
              </span>
            </Link>
            <span className="text-qcloud-muted">/</span>
            <Link to="/arena" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Arena</Link>
            <span className="text-qcloud-muted">/</span>
            <span className="font-semibold text-qcloud-text">{track?.title || trackId}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/arena" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">All Tracks</Link>
            <Link to="/hardware" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Hardware</Link>
            <AuthHeader />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-10 px-4">
        {isLoading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-qcloud-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Track header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors.badge}`}>
                  {track?.title}
                </span>
                <span className="text-sm text-qcloud-muted">
                  {track?.challenge_count || challenges.length} challenges
                </span>
              </div>
              {track?.description && (
                <p className="text-qcloud-muted max-w-2xl">{track.description}</p>
              )}
            </div>

            {/* Advanced track: QEC challenge (quantum memory system) */}
            {trackId === 'advanced' && (
              <div className="mb-8">
                <Link
                  to="/memory/c12bbef1-12db-430b-b07a-d8d54ed4e1c2"
                  className="block bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 p-6 hover:border-purple-400 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center text-2xl flex-shrink-0">
                      🛡️
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg text-purple-800 group-hover:text-purple-900">
                          Distance-3 Surface Code Optimization
                        </h3>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">hard</span>
                      </div>
                      <p className="text-sm text-qcloud-muted mb-3">
                        Optimize the stabilizer measurement circuit, scheduling, and physical qubit layout for a d=3 rotated surface code.
                        Compete for the lowest logical error rate on real IBM quantum hardware.
                      </p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> Stim / ScaLER Simulator</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" /> IBM Hardware</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400" /> PyMatching Decoder</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Challenge list */}
            {challenges.length === 0 && trackId !== 'advanced' ? (
              <div className="bg-white rounded-xl border border-qcloud-border p-12 text-center">
                <div className="text-4xl mb-3">🏗️</div>
                <p className="text-qcloud-muted text-lg">No challenges in this track yet</p>
                <p className="text-sm text-qcloud-muted mt-2">Check back soon — new challenges are being added.</p>
              </div>
            ) : challenges.length > 0 ? (
              <div className="space-y-4">
                {challenges.map((c, index) => (
                  <Link
                    key={c.id}
                    to={`/challenge/${c.id}`}
                    className="block bg-white rounded-xl border border-qcloud-border p-6 hover:border-qcloud-primary/50 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start gap-4">
                      {/* Number */}
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${colors.accent} bg-slate-50 group-hover:bg-slate-100 transition-colors flex-shrink-0`}>
                        {index + 1}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-qcloud-text group-hover:text-qcloud-primary transition-colors">
                            {c.title}
                          </h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${DIFFICULTY_COLORS[c.difficulty] || DIFFICULTY_COLORS.medium}`}>
                            {c.difficulty}
                          </span>
                        </div>

                        {c.description && (
                          <p className="text-sm text-qcloud-muted line-clamp-2 mb-3">
                            {c.description
                              .replace(/^#{1,3}\s+/gm, '')
                              .replace(/\*\*([^*]+)\*\*/g, '$1')
                              .replace(/`([^`]+)`/g, '$1')
                              .replace(/^[-*]\s+/gm, '')
                              .replace(/\n+/g, ' ')
                              .trim()}
                          </p>
                        )}

                        <div className="flex items-center gap-4 text-xs text-qcloud-muted">
                          {/* Supported languages */}
                          {c.language_whitelist && c.language_whitelist.length > 0 && (
                            <div className="flex items-center gap-1">
                              <span>Languages:</span>
                              {c.language_whitelist.map(lang => (
                                <span key={lang} className="px-1.5 py-0.5 bg-slate-100 rounded font-medium">
                                  {lang}
                                </span>
                              ))}
                            </div>
                          )}

                          <span>{c.total_participants || 0} participants</span>

                          {c.top_score != null && (
                            <span className={`font-medium ${colors.accent}`}>
                              Top: {(c.top_score * 100).toFixed(1)}%
                            </span>
                          )}
                        </div>

                        {c.tags && c.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {c.tags.map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 bg-slate-50 rounded text-xs text-qcloud-muted">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Arrow */}
                      <div className="text-qcloud-muted group-hover:text-qcloud-primary transition-colors flex-shrink-0 mt-1">
                        &rarr;
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  )
}

export default TrackPage
